import Group from 'components/utils/Group'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import moment from 'moment'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import FormatCurrency from 'components/Common/FormatCurrency'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { rem } from 'polished'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import useCopyWithFeedback from 'hooks/useCopyWithFeedback'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import TuroModalImage from 'luxPlus/components/PartnerPromoCampaign/TuroCampaign/TuroModalImage'

const PromoClickableListItem = styled(ClickableListItem)`
  background-color: ${props => props.theme.palette.neutral.default.six};
  max-width: ${rem(336)};
`

interface Props {
  benefit?: App.MemberSubscriptionBenefitItem
}

function LuxPlusTuroCampaignModal({ benefit }: Props) {
  const intl = useIntl()
  const code = benefit?.code
  const expirationDate = benefit?.expirationDate
  const formattedExpirationDate = expirationDate ? moment(expirationDate).format(DMY_CASUAL_FORMAT) : undefined

  const { isCopied, handleCopy } = useCopyWithFeedback(code ?? '')

  const titleText = <FormattedMessage
    id="8YiVCNzIAyLdlLUw2Wbm"
    defaultMessage="Save {amount} on your first {brandName} booking"
    description="Title for Turo partner campaign modal showing discount amount"
    values={{
      amount: <FormatCurrency value={100} format="rounded" />,
      brandName: 'Turo',
    }}
  />

  const visitButtonText = intl.formatMessage(
    {
      id: 'Eag-mrnHRf12NJBS7zht',
      defaultMessage: 'Visit {brandName} Website',
      description: 'Button text to visit the Turo website from partner campaign modal',
    },
    { brandName: 'Turo' },
  )

  return (
    <Modal
      size="L"
      title={titleText}
      primaryActionText={benefit ? visitButtonText : undefined}
      primaryActionHref="https://turo.com/au/en?utm_source=referral&utm_medium=referral&utm_campaign=ref:re:wu:pro:conv:demgen:luxury_escapes:int_au:en:Q3:2025"
      primaryActionTarget="_blank"
    >
      <Group direction="vertical" gap={20}>
        <TuroModalImage />
        <div>
          <BodyText variant="medium">
            <FormattedMessage
              id="5cP3urIxQgRZ4JZxo32B"
              defaultMessage="Skip the rental car counter and rent just about any car, just about anywhere. Receive {discountAmount} off your first Turo reservation in Australia, for car rentals over {minimumAmount}."
              description="Introductory text and offer details for Turo car rental campaign for LuxPlus+ members"
              values={{
                discountAmount: <FormatCurrency value={100} format="rounded" />,
                minimumAmount: <FormatCurrency value={150} format="rounded" />,
              }}
            />
            <br />
            <br />
            {intl.formatMessage({
              id: 'U25qUfB3Wp-_PsmDJQNN',
              defaultMessage: 'Before you check out on the Turo website, tap "Enter promo code" and add the code below to save.',
              description: 'Instructions for applying the Turo promo code during checkout on the Turo website',
            })}
          </BodyText>
        </div>
        {code && <>
          <PromoClickableListItem
            title={<b>{code}</b>}
            endIcon={isCopied ? <LineCheckIcon size="S" /> : <LineCopyIcon size="S" />}
            onClick={handleCopy}
            selected={isCopied}
          />
          {formattedExpirationDate && <BodyText variant="medium">
            <FormattedMessage
              id="SuJstVPHNqqw5f3KOM_n"
              defaultMessage="<bold>Expires:</bold> {date}"
              description="Expiration date label for Turo partner promo code in a modal dialog"
              values={{
                bold: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                date: formattedExpirationDate,
              }}
            />
          </BodyText>}
        </>}

        <BodyText variant="small">
          <FormattedMessage
            id="Yj6CEJ64mpN3xeFs6OsU"
            defaultMessage="Promo code is valid in Australia only for a single use per customer. <link>Terms and conditions apply.</link>"
            description="Disclaimer and terms link for Turo partner promo code"
            values={{
              link: (chunks: React.ReactNode) => <TextLink target="_blank" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`} weight="regular">{chunks}</TextLink>,
            }}
          />
        </BodyText>
      </Group>
    </Modal>
  )
}

export default LuxPlusTuroCampaignModal
